import {
  Component,
  computed,
  effect,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  OnDestroy,
  OnInit,
  Output,
  signal,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { ThemeService } from '../services/theme';
import { MobileModalService } from '../services/mobile-modal';
import { ModalOverlayService } from '../services/modal-overlay';
import { ResponsiveService } from '../services/responsive';
import { SnowplowService } from '../services/snowplow';
import { makeEvent } from '../analytics/builder';
import { focusableElements, trapTab } from '../services/focus-trap';

// Own key so V2 dismissals (explanation_modal_{asset,main}_v2_v0) don't suppress V3's onboarding.
export const V3_ONBOARDING_STORAGE_KEY = 'explanation_modal_v3_v0';

// Product-confirmed copy (du-form). Slides 1–2 carry a subtitle, slide 3 the terms small print.
const STEPS: { title: string; subtitle?: string; description: string }[] = [
  {
    title: 'Die AI Aktienanalyse',
    subtitle: 'Verstehen, bevor du entscheidest.',
    description:
      'News, Analysten, Earnings, Wettbewerb und Fundamentaldaten zu jedem Wert, der dich interessiert. Du liest in Minuten, was sonst Stunden Recherche kostet. Damit du weißt, warum sich ein Kurs bewegt.',
  },
  {
    title: 'Institutionelle Qualität',
    subtitle: 'Daten, die sonst Profis vorbehalten sind.',
    description:
      'Hinter jeder Analyse stehen professionelle Datenquellen: FactSet, Quartr, TipRanks und TraderFox. Kennzahlen, Schätzungen, Earnings Calls und Analystenstimmen, gebündelt an einer Stelle.',
  },
  {
    title: 'Wichtige Hinweise',
    description:
      'Du verlässt die Anwendung der finanzen.net zero GmbH. Die AI Analysen werden von der finanzen.net GmbH bereitgestellt und angezeigt. Die finanzen.net zero GmbH stellt diese Inhalte nicht selbst bereit und übernimmt keine Verantwortung für deren Inhalt.',
  },
];

@Component({
  selector: 'app-v3-onboarding-modal',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './v3-onboarding-modal.html',
  styleUrl: './v3-onboarding-modal.scss',
})
export class V3OnboardingModal implements OnInit, OnDestroy {
  private readonly themeService = inject(ThemeService);
  private readonly modalService = inject(MobileModalService);
  private readonly modalOverlay = inject(ModalOverlayService);
  private readonly responsive = inject(ResponsiveService);
  private readonly snowplow = inject(SnowplowService);

  readonly visible = signal(false);
  @Output() cancelled = new EventEmitter<void>();
  private sub?: Subscription;
  private hasTrackedView = false;

  private readonly dialog = viewChild<ElementRef<HTMLElement>>('dialog');
  private previouslyFocused: Element | null = null;

  // Runs once the dialog mounts: move focus in, remember where it came from.
  private readonly focusOnOpen = effect(() => {
    const dialog = this.dialog()?.nativeElement;
    if (!dialog) return;
    this.previouslyFocused = document.activeElement;
    (focusableElements(dialog)[0] ?? dialog).focus();
  });

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const dialog = this.dialog()?.nativeElement;
    if (!dialog) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancel();
    } else if (event.key === 'Tab') {
      trapTab(dialog, event);
    }
  }

  readonly steps = STEPS;
  readonly step = signal(0);
  readonly isLastStep = computed(() => this.step() === STEPS.length - 1);
  readonly progressPercent = computed(() => ((this.step() + 1) / STEPS.length) * 100);

  // One design for Zero and Finnet alike — only a light/dark split.
  get isDark() {
    return this.themeService.isDark();
  }

  get logo() {
    return this.isDark ? '/v3/finanzenDark.svg' : '/v3/finanzenLight.svg';
  }

  get stepImage() {
    return this.imagePath(this.step() + 1);
  }

  private imagePath(step: number): string {
    const size = this.responsive.isMobile() ? '' : '_large';
    return `/v3/onboarding/${this.isDark ? 'dark' : 'light'}/step${step}${size}.png`;
  }

  ngOnInit(): void {
    this.modalService.init(V3_ONBOARDING_STORAGE_KEY);
    this.sub = this.modalService.visible$.subscribe((v) => {
      this.visible.set(v);
      if (v) {
        this.modalOverlay.setBlockingOverlay(true);
        this.preloadStepImages();

        if (!this.hasTrackedView) {
          this.hasTrackedView = true;
          this.trackStepViewed(1);
        }
      }
    });
  }

  // Prefetch so step changes animate an already-loaded image (the mount itself is load-gated).
  private imagesPreloaded = false;
  private preloadStepImages(): void {
    if (this.imagesPreloaded) return;
    this.imagesPreloaded = true;
    for (let i = 2; i <= STEPS.length; i++) {
      new Image().src = this.imagePath(i);
    }
  }

  // The slide-in must not run on a still-loading image (cold-cache first mount).
  visualLoaded(event: Event): void {
    (event.target as HTMLElement).classList.add('loaded');
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    // Core can destroy an open modal (e.g. auth resolving into guest mode) — release the overlay
    // and restore focus, same as a normal dismiss.
    if (this.visible()) {
      this.modalOverlay.setBlockingOverlay(false);
      (this.previouslyFocused as HTMLElement | null)?.focus();
      this.previouslyFocused = null;
    }
  }

  next(): void {
    if (this.isLastStep()) {
      this.dismiss();
    } else {
      this.step.set(this.step() + 1);
      this.trackStepViewed(this.step() + 1);
    }
  }

  // Close button, Abbrechen and Escape: Core sends GO_BACK to the parent. The modal stays up
  // and the show-once key stays unset, so declining never reveals the analysis.
  cancel(): void {
    this.cancelled.emit();
  }

  dismiss(): void {
    this.modalService.dismissForever();
    this.modalOverlay.setBlockingOverlay(false);
    (this.previouslyFocused as HTMLElement | null)?.focus();
    this.previouslyFocused = null;
  }

  // Same event as V2's MobileWelcomeModal, but with the real step number (V2 hardcodes
  // screen_step: 2 since it only ever has 2 steps — V3 has 3) and gated on trackerReady()
  // (track() no-ops until the tracker finishes its async setup, so an early click could
  // otherwise drop the event — V2's incrementStep() has this same latent gap, unfixed here).
  private trackStepViewed(step: number): void {
    void this.snowplow.trackerReady().then(() => {
      this.snowplow.track(
        makeEvent('element_viewed', {
          element_title: 'launch_info_screen',
          feature_name: 'page',
          property: { screen_step: step },
        }),
      );
    });
  }
}
